"use client";

import { ArrowRight, Check, Copy, FileInput, FileOutput, Terminal } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cppFileIoSnippet, type ProblemFileIo } from "@/features/judge/lib/file-io";
import { cn } from "@/lib/utils";

/**
 * `EX-RTW05`: the box over a statement whose program reads and writes files.
 * What a contestant must know before reading the rest: where the input comes
 * from, where the answer goes, and that stdout is not read when a file is.
 * The same box on the practice, contest, lesson and quiz pages (`dark` on the
 * quiz's dark workspace); a stdin/stdout problem never renders it.
 */
export function FileIoNotice({
  fileIo,
  className,
  tone = "light",
}: {
  fileIo: ProblemFileIo;
  className?: string;
  tone?: "light" | "dark";
}) {
  const dark = tone === "dark";
  const t = useTranslations("judge.fileIo");
  const [copied, setCopied] = useState(false);
  const snippet = cppFileIoSnippet(fileIo);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // A blocked clipboard leaves the lines on screen to copy by hand.
    }
  };

  return (
    <section
      aria-labelledby="file-io-notice-title"
      className={cn(
        "grid gap-3 rounded-xl border p-4",
        dark ? "border-white/15 bg-white/[0.04]" : "border-primary/25 bg-gradient-to-br from-primary/[0.06] to-white",
        className,
      )}
      data-testid="file-io-notice"
    >
      <h2
        id="file-io-notice-title"
        className={cn("flex items-center gap-2 text-[15px] font-bold", dark ? "text-white" : "text-neutral-900")}
      >
        <FileInput className="h-4 w-4 text-primary" aria-hidden />
        {t("title")}
      </h2>

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <FileChip name={fileIo.inputFile} fallback={t("stdin")} icon="in" dark={dark} />
        <ArrowRight className="h-3.5 w-3.5 text-neutral-300" aria-hidden />
        <span
          className={cn(
            "rounded-md border px-2 py-1 font-medium",
            dark ? "border-white/15 bg-white/[0.06] text-white/80" : "border-neutral-200 bg-white text-neutral-700",
          )}
        >
          {t("program")}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-neutral-300" aria-hidden />
        <FileChip name={fileIo.outputFile} fallback={t("stdout")} icon="out" dark={dark} />
      </div>

      <ul className={cn("grid list-disc gap-1 pl-5 text-sm leading-6", dark ? "text-white/80" : "text-neutral-700")}>
        {fileIo.inputFile ? <li>{t("readFrom", { name: fileIo.inputFile })}</li> : null}
        {fileIo.outputFile ? <li>{t("writeTo", { name: fileIo.outputFile })}</li> : null}
      </ul>

      <div className="grid gap-1.5">
        <p className={cn("text-xs", dark ? "text-white/50" : "text-[rgb(var(--muted-foreground))]")}>{t("tip")}</p>
        <div className="flex items-start gap-2 rounded-lg border border-neutral-200 bg-neutral-950 p-2.5">
          <pre className="min-w-0 flex-1 overflow-x-auto font-mono text-xs leading-5 text-neutral-100" data-testid="file-io-snippet">
            {snippet}
          </pre>
          <Button
            type="button"
            variant="tertiary"
            onClick={copy}
            aria-label={copied ? t("copied") : t("copy")}
            className="inline-flex min-h-8 shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-neutral-300 hover:bg-white/10 hover:text-white"
          >
            {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
            {copied ? t("copied") : t("copy")}
          </Button>
        </div>
      </div>
    </section>
  );
}

function FileChip({ name, fallback, icon, dark }: { name?: string; fallback: string; icon: "in" | "out"; dark: boolean }) {
  if (!name) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-md border px-2 py-1 font-medium",
          dark ? "border-white/15 bg-white/[0.06] text-white/60" : "border-neutral-200 bg-white text-neutral-500",
        )}
      >
        <Terminal className="h-3.5 w-3.5" aria-hidden />
        {fallback}
      </span>
    );
  }
  const Icon = icon === "in" ? FileInput : FileOutput;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-1 font-mono font-semibold",
        dark ? "border-primary/60 bg-primary/15 text-orange-200" : "border-primary/40 bg-primary/[0.06] text-primary-ink",
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {name}
    </span>
  );
}

/** `EX-RTW05`: the "File I/O" tag beside the limits, a property of the problem like "Runs twice". */
export function FileIoTag({ fileIo }: { fileIo: ProblemFileIo }) {
  const t = useTranslations("judge.fileIo");
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-primary/25 bg-primary/[0.08] px-2.5 py-0.5 text-xs font-semibold text-primary-ink"
      title={t("tagTitle", { files: [fileIo.inputFile, fileIo.outputFile].filter(Boolean).join(", ") })}
      data-testid="file-io-tag"
    >
      <FileOutput className="h-3.5 w-3.5" aria-hidden />
      {t("tag")}
    </span>
  );
}
